import React from 'react';
import { motion } from 'framer-motion';
import { ChefHat, Brain, Sparkles } from 'lucide-react';
import { Product } from '../../types/Product';
import { useGrokMenuGeneration } from '../../hooks/shopping/useGrokMenuGeneration';

interface MenuGeneratorProps {
  products: Product[];
  budget: number;
  dishCount: number;
}

export const MenuGenerator: React.FC<MenuGeneratorProps> = ({
  products,
  budget,
  dishCount
}) => {
  const { 
    isGenerating,
    error,
    menu,
    generateMenu
  } = useGrokMenuGeneration();

  return (
    <div className="mt-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="text-orange-500" size={24} />
          <div>
            <h3 className="text-lg font-semibold">Generador de Menús IA</h3>
            <p className="text-sm text-gray-600">
              Genera un menú personalizado con tus productos
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <ChefHat size={16} className="text-orange-500" />
          <span>{dishCount} platos</span>
          <span className="mx-2">•</span>
          <Sparkles size={16} className="text-orange-500" />
          <span>{budget}€ presupuesto</span>
        </div>
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => generateMenu(products, budget, dishCount)}
        disabled={isGenerating || products.length === 0}
        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 px-6 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
      >
        {isGenerating ? (
          <>
            <Brain className="animate-spin" size={20} />
            Generando menú personalizado...
          </>
        ) : (
          <>
            <ChefHat size={20} />
            {products.length === 0 
              ? 'Añade productos para generar un menú'
              : 'Generar menú con IA'}
          </>
        )}
      </motion.button>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 text-red-600 p-4 rounded-lg"
        >
          {error}
        </motion.div>
      )}

      {menu && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-6 shadow-lg"
        >
          <div className="flex items-center gap-2 mb-4">
            <ChefHat className="text-orange-500" size={24} />
            <h4 className="text-xl font-semibold">Tu Menú Personalizado</h4>
          </div>
          <div 
            className="prose prose-orange max-w-none"
            dangerouslySetInnerHTML={{ __html: menu }} 
          />
        </motion.div>
      )}
    </div>
  );
};